import { supabase } from '@/lib/supabase';
import type { UserProfile } from '../types/auth.types';

// Timeout helper
export const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  timeoutMessage = 'Operation timed out'
): Promise<T> => {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error(timeoutMessage)), ms)
  );
  return Promise.race([promise, timeout]);
};

class AuthService {
  /**
   * Sign in with email and password
   */
  async signIn(
    email: string,
    password: string
  ): Promise<{ user: UserProfile | null; error: string | null }> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
        return { user: null, error: this.getAuthErrorMessage(error.message) };
      }

      if (!data.user) {
        return { user: null, error: 'Failed to sign in' };
      }

      // Fetch user profile
      const user = await this.fetchUserProfile(data.user.id);

      if (!user) {
        return { user: null, error: 'User profile not found. Please contact support.' };
      }

      return { user, error: null };
    } catch (error) {
      console.error('Sign in exception:', error);
      return { user: null, error: 'An unexpected error occurred during sign in' };
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.error('Sign out error:', error);
        return { error: error.message };
      }
      return { error: null };
    } catch (error) {
      console.error('Sign out exception:', error);
      return { error: 'An unexpected error occurred during sign out' };
    }
  }

  /**
   * Get current session
   */
  async getSession() {
    try {
      const result = await withTimeout(
        supabase.auth.getSession(),
        10000,
        'Session fetch timed out'
      );

      if (result.error) {
        console.error('Get session error:', result.error);
        return { session: null, error: result.error.message };
      }
      return { session: result.data.session, error: null };
    } catch (error) {
      console.error('Get session exception:', error);
      return {
        session: null,
        error: error instanceof Error ? error.message : 'Failed to get session',
      };
    }
  }

  /**
   * Fetch user profile using RPC function
   * The RPC function needs to know which schema to use
   */
  async fetchUserProfile(authUserId: string): Promise<UserProfile | null> {
    try {
      // Call the schema-appropriate RPC function
      // The function exists in both public and testing schemas
      const { data, error } = await supabase.rpc('get_current_user_profile');

      if (error) {
        console.error('Fetch user profile error:', error);
        return null;
      }

      if (!data || (Array.isArray(data) && data.length === 0)) {
        console.warn('No user profile found for auth user:', authUserId);
        return null;
      }

      // RPC returns an array, get first result
      const profile = Array.isArray(data) ? data[0] : data;

      return profile as UserProfile;
    } catch (error) {
      console.error('Fetch user profile exception:', error);
      return null;
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<{ error: string | null }> {
    try {
      const redirectUrl = this.getPasswordResetRedirectUrl();

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('Password reset error:', error);
        return { error: this.getAuthErrorMessage(error.message) };
      }

      return { error: null };
    } catch (error) {
      console.error('Password reset exception:', error);
      return { error: 'Failed to send password reset email. Please try again.' };
    }
  }

  /**
   * Update password
   */
  async updatePassword(newPassword: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        console.error('Update password error:', error);
        return { error: error.message };
      }

      return { error: null };
    } catch (error) {
      console.error('Update password exception:', error);
      return { error: 'Failed to update password' };
    }
  }

  /**
   * Get password reset redirect URL based on environment
   */
  private getPasswordResetRedirectUrl(): string {
    const env = import.meta.env.VITE_ENV;

    const urls: Record<string, string> = {
      development: 'http://localhost:3000/reset-password',
      testing: 'https://testing--nailedit-renos.netlify.app/reset-password',
      production: 'https://nailedit-renos.netlify.app/reset-password',
    };

    return urls[env] || urls.development;
  }

  /**
   * Get user-friendly error messages
   */
  private getAuthErrorMessage(errorMessage: string): string {
    const errorMessages: Record<string, string> = {
      'Invalid login credentials': 'Invalid email or password. Please try again.',
      'Email not confirmed': 'Please verify your email before signing in.',
      'User not found': 'No account found with this email address.',
      'Password recovery requires an email': 'Please enter your email address.',
      'Email rate limit exceeded': 'Too many attempts. Please try again later.',
    };

    return errorMessages[errorMessage] || errorMessage;
  }
}

export const authService = new AuthService();